import React, { useState, useMemo } from 'react';
import { Select, Checkbox } from 'antd';
import { getIndicatorName } from '../utils/indicatorName';

/**
 * IndicatorSelector Component
 * A reusable multi-select component for indicators with:
 * - Search functionality
 * - Select All checkbox for filtered results
 * - Support for custom styling and handlers
 * 
 * @param {Array} indicators - Array of indicator objects with ind_code and ind_name_eng
 * @param {Array} selectedIndicators - Currently selected indicator values
 * @param {Function} onChange - Callback when selection changes
 * @param {Object} style - Custom styles for the Select component
 * @param {Boolean} showSearch - Show search input (default: true)
 * @param {Number} minWidth - Minimum width of the component
 */
export const IndicatorSelector = ({
  indicators = [],
  selectedIndicators = [],
  onChange,
  style = {},
  showSearch = true,
  minWidth = '500px',
  placeholder = 'Select Indicators',
  disabled = false
}) => {
  const [indicatorSearchFilter, setIndicatorSearchFilter] = useState('');

  // Generate indicator options from the indicators array
  const indicatorOptions = useMemo(() => {
    return indicators
      .sort((a, b) => a.ind_code - b.ind_code)
      .map(indicator => {
        const isPrivate = indicator.is_private === 1;
        const baseLabel = `${indicator.ind_code} - ${getIndicatorName(indicator)}`;
        return {
          label: isPrivate ? `${baseLabel} (Private)` : baseLabel,
          value: baseLabel,
          isPrivate
        };
      });
  }, [indicators]);

  // Handle indicator selection with sorting
  const handleIndicatorChange = (values) => {
    const sorted = [...values].sort((a, b) => {
      return parseInt(a, 10) - parseInt(b, 10);
    });
    onChange(sorted);
  };

  // Get filtered options based on search input
  const getFilteredOptions = () => {
    return indicatorOptions.filter(option =>
      option.label.toLowerCase().includes(indicatorSearchFilter.toLowerCase())
    );
  };

  const filteredOptions = getFilteredOptions();
  const allFiltered = filteredOptions.length > 0 && filteredOptions.every(opt => selectedIndicators.includes(opt.value));

  return (
    <Select
      mode="multiple"
      showSearch={showSearch}
      onChange={handleIndicatorChange}
      placeholder={placeholder}
      value={selectedIndicators}
      style={{ minWidth, marginBottom: '5px', marginRight: '10px', ...style }}
      options={indicatorOptions}
      filterOption={(input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase())
      }
      optionFilterProp="label"
      autoClearSearchValue={false}
      onSearch={setIndicatorSearchFilter}
      disabled={disabled}
      dropdownRender={(menu) => (
        <>
          <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <Checkbox
              checked={allFiltered}
              indeterminate={filteredOptions.some(opt => selectedIndicators.includes(opt.value)) && !allFiltered}
              onChange={(e) => {
                if (e.target.checked) {
                  handleIndicatorChange([...new Set([...selectedIndicators, ...filteredOptions.map(opt => opt.value)])]);
                } else {
                  handleIndicatorChange(selectedIndicators.filter(ind => !filteredOptions.map(opt => opt.value).includes(ind)));
                }
              }}
            >
              <span style={{ fontSize: '12px' }}>Select All in Results</span>
            </Checkbox>
          </div>
          {menu}
        </>
      )}
    />
  );
};

export default IndicatorSelector;
